import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { Car, Loader2, User, Users } from "lucide-react";
import { useAuth, homeFor } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AuthShell, Field } from "@/pages/Login";

const ROLES = [
  { value: "rider", label: "Ride", sub: "Book trips", icon: User },
  { value: "driver", label: "Drive", sub: "Apply to drive", icon: Car },
  { value: "both", label: "Both", sub: "Ride + drive", icon: Users },
];

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    account_type: ["driver", "both"].includes(params.get("role")) ? params.get("role") : "rider", name: "", email: "", phone: "", password: "", vehicle: "", vehicle_class: "xl",
    license_number: "", license_state: "TX", insurance_provider: "", insurance_policy: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;
  const drives = form.account_type !== "rider";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, phone: form.phone || null };
      if (!drives) ["vehicle", "vehicle_class", "license_number", "license_state", "insurance_provider", "insurance_policy"].forEach((k) => { payload[k] = null; });
      const u = await register(payload);
      navigate(homeFor(u));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Join Scoopz" sub="One account, every ride around Central Texas.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-3 gap-3">
          {ROLES.map((r) => (
            <button key={r.value} type="button" onClick={() => setForm({ ...form, account_type: r.value })} data-testid={`register-role-${r.value}`}
              className={`rounded-2xl border-2 p-4 text-left transition-colors ${form.account_type === r.value ? "border-primary bg-primary/5" : "border-border hover:border-foreground/30"}`}>
              <r.icon className={`h-5 w-5 ${form.account_type === r.value ? "text-primary" : "text-muted-foreground"}`} />
              <p className="mt-2 font-display font-semibold">{r.label}</p>
              <p className="text-xs text-muted-foreground">{r.sub}</p>
            </button>
          ))}
        </div>
        <Field id="name" label="Full name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
        <Field id="email" label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
        <Field id="phone" label="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} data-testid="register-phone-input" />
        <Field id="password" label="Password (8+ characters)" type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="register-password-input" />
        {drives && (
          <div className="grid grid-cols-2 gap-3" data-testid="driver-application-fields">
            <Field id="license_number" label="Driver's license #" required value={form.license_number} onChange={set("license_number")} data-testid="register-license-input" />
            <Field id="license_state" label="License state" value={form.license_state} onChange={set("license_state")} data-testid="register-license-state-input" />
            <Field id="insurance_provider" label="Insurance provider" value={form.insurance_provider} onChange={set("insurance_provider")} data-testid="register-insurance-input" />
            <Field id="insurance_policy" label="Policy #" value={form.insurance_policy} onChange={set("insurance_policy")} data-testid="register-policy-input" />
          </div>
        )}
        {drives && (
          <div className="space-y-2" data-testid="register-vehicle-class">
            <p className="text-sm font-medium">Vehicle type</p>
            <div className="grid grid-cols-2 gap-2">
              {[["xl", "XL", "SUV / minivan, 6+ passengers"], ["sedan", "Sedan", "Not accepting yet"]].map(([v, l, s]) => (
                <button key={v} type="button" disabled={v === "sedan"} onClick={() => setForm({ ...form, vehicle_class: v })} data-testid={`register-vehicle-class-${v}`}
                  className={`rounded-xl border-2 p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${form.vehicle_class === v ? "border-primary bg-primary/5" : "border-border"}`}>
                  <p className="font-semibold">{l}</p><p className="text-xs text-muted-foreground">{s}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {drives && (
          <Field id="vehicle" label="Vehicle (make, model, color, plate)" required value={form.vehicle} onChange={set("vehicle")}
            placeholder="e.g. Black Chevy Suburban · ABC-1234" data-testid="register-vehicle-input" />
        )}
        {error && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base font-semibold" data-testid="register-submit-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : form.account_type === "rider" ? "Create account" : "Create account & apply to drive"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="go-to-login-link">Log in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
